import { MeshProps, ThreeEvent } from '@react-three/fiber'
import { useRef, Suspense } from 'react'
import { Vector3, Vector2, Plane } from 'three'
import { POSITIVE_Z } from '~/components/DrawingCanvas/constants/dimensions'
import { RotateIconTexture } from '~/components/DrawingCanvas/Products/components/RotationHandle'
import { isLeftMouseButton } from '~/components/DrawingCanvas/util/eventHandlers'

export function RotationHandle({
  center,
  onRotate,
  onCommit,
  visible,
  ...props
}: MeshProps & { center: Vector3; onRotate?: (angle: number) => void; onCommit?: (angle: number) => void }) {
  const centerPoint = new Vector2().copy(center)
  const v1 = new Vector2(1, 0)
  const active = useRef<number | null>(null)
  const origin = useRef<Vector3 | null>(null)
  const plane = new Plane()
  const target = new Vector3()

  const getAngleDelta = (e: ThreeEvent<PointerEvent>) => {
    if (!origin.current || e.pointerId != active.current) return
    plane.setFromNormalAndCoplanarPoint(POSITIVE_Z, origin.current)
    e.ray.intersectPlane(plane, target)
    const { x, y } = target
    const v2 = new Vector2().copy({ x, y }).sub(centerPoint).normalize()
    return v2.y < 0 ? Math.PI * 2 - v1.angleTo(v2) : v1.angleTo(v2)
  }

  return (
    <mesh
      {...props}
      visible={visible}
      onPointerDown={e => {
        if (!visible) return
        e.stopPropagation()
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
        active.current = e.pointerId
        origin.current = e.point
      }}
      onPointerMove={e => {
        if (!visible || isLeftMouseButton(e.buttons)) return
        e.stopPropagation()
        const angle = getAngleDelta(e)
        if (angle === undefined) return
        onRotate?.(angle)
      }}
      onPointerUp={e => {
        if (!visible) return
        e.stopPropagation()
        ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
        const angle = getAngleDelta(e)
        if (angle === undefined) return
        onCommit?.(angle)
        active.current = null
        origin.current = null
      }}
    >
      <cylinderGeometry args={[16, 16]} />
      <Suspense fallback={<meshBasicMaterial color="white" />}>
        <RotateIconTexture />
      </Suspense>
    </mesh>
  )
}
